/**
 * IMPORTANT: These components are declared in their own module (never inside
 * another component's function body) on purpose. Defining components inside
 * another component's function body causes React to see a brand-new component
 * type on every render, which unmounts/remounts the DOM every time state changes
 * (e.g. on every slider onChange). That remount interrupts the browser's native
 * drag/pointer-capture on <input type="range">, which is what made the sliders
 * feel like they were "stuck" on every step instead of moving smoothly. Keeping
 * them at module scope keeps their identity stable across renders so dragging
 * works normally.
 */

type ConfigurationNumberInputProps = {
  value: number | null;
  disabled?: boolean;
  suffix: string;
  min: number;
  max: number;
  step: number;
  onValueChange: (value: any) => void;
  onActivate: () => void;
  className?: string;
  error?: string;
};

/**
 * Number input + suffix pill rendered inside the card header (e.g. `12 sec`).
 * When the value is null (or explicitly disabled) the control is inert and a
 * click activates it via `onActivate` (used to seed a default value).
 */
function ConfigurationNumberInput({
  value,
  disabled = false,
  suffix,
  min,
  max,
  step,
  onValueChange,
  onActivate,
  className,
  error,
}: ConfigurationNumberInputProps) {
  const isDisabled = disabled || value == null;

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const raw = input.value;
    if (raw.length > 1 && /^0\d+$/.test(raw)) {
      const sanitized = raw.replace(/^0+/, "") || "0";
      input.value = sanitized;
    }
  };

  return (
    <div className={`configuration-mini-card__value-wrapper ${className ?? ""}`}>
      <label className={`configuration-mini-card__value-input ${error ? "configuration-mini-card__value-input--error" : ""}`}>
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={isDisabled ? "" : value ?? 0}
          placeholder="--"
          onChange={(event) => {
            if (isDisabled) return;
            onValueChange(event?.target?.value);
          }}
          onInput={handleInput}
          readOnly={isDisabled}
          onClick={isDisabled ? onActivate : undefined}
        />
        <span>{suffix}</span>
      </label>
      {error && <div className="error-message">{error}</div>}
    </div>
  );
}

type ConfigurationRangeSliderProps = {
  value: number | null;
  disabled?: boolean;
  min: number;
  max: number;
  step: number;
  onValueChange: (value: any) => void;
  onActivate: () => void;
};

/**
 * Full-width range slider. This must be a sibling of (not a child of) the card
 * header so it stretches across the whole card with `width: 100%`.
 */
function ConfigurationRangeSlider({
  value,
  disabled = false,
  min,
  max,
  step,
  onValueChange,
  onActivate,
}: ConfigurationRangeSliderProps) {
  const isDisabled = disabled || value == null;

  return (
    <input
      className="configuration-range"
      type="range"
      min={min}
      max={max}
      step={step}
      value={value ?? 0}
      onChange={(event) => {
        if (isDisabled) return;
        onValueChange(event?.target?.value);
      }}
      disabled={isDisabled}
      onClick={isDisabled ? onActivate : undefined}
    />
  );
}

export type ConfigurationSliderCardProps = {
  title: string;
  subtitle?: string;
  suffix: string;
  value: number | null;
  min: number;
  max: number;
  step: number;
  icon?: string;
  onValueChange: (value: any) => void;
  onClick: () => void;
  error?: string;
};

export function ConfigurationSliderCard({
  title,
  subtitle,
  icon,
  value,
  suffix,
  min,
  max,
  step,
  onValueChange,
  onClick,
  error,
}: ConfigurationSliderCardProps) {
  return (
    <div
      className="configuration-mini-card"
      onClick={value == null ? onClick : undefined}
    >
      <div className="configuration-mini-card__header">
        <div>
          <div className="d-inline-flex gap-2">
            {icon && <img src={icon} alt="" />}
            <h4>{title}</h4>
          </div>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <ConfigurationNumberInput
          value={value}
          suffix={suffix}
          min={min}
          max={max}
          step={step}
          onValueChange={onValueChange}
          onActivate={onClick}
          error={error}
        />
      </div>
      <ConfigurationRangeSlider
        value={value}
        min={min}
        max={max}
        step={step}
        onValueChange={onValueChange}
        onActivate={onClick}
      />
    </div>
  );
}

export type ConfigurationInputCardProps = {
  title: string;
  subtitle: string;
  value: number;
  suffix: string;
  min: number;
  max: number;
  step: number;
  disabled: boolean;
  onValueChange: (value: any) => void;
  onClick: () => void;
  error?: string;
};

export function ConfigurationInputCard({
  title,
  subtitle,
  value,
  suffix,
  min,
  max,
  step,
  disabled,
  onValueChange,
  onClick,
  error,
}: ConfigurationInputCardProps) {
  return (
    <div
      onClick={disabled ? onClick : undefined}
      className={`sensor-threshold ${disabled ? "sensor-threshold--disabled" : ""}`}
    >
      <div className="sensor-threshold__header">
        <div>
          <div className="sensor-threshold__title">{title}</div>
          <div className="sensor-threshold__subtitle">{subtitle}</div>
        </div>
        <ConfigurationNumberInput
          value={value}
          disabled={disabled}
          suffix={suffix}
          min={min}
          max={max}
          step={step}
          onValueChange={onValueChange}
          onActivate={onClick}
          className="sensor-threshold__input"
          error={error}
        />
      </div>
      <ConfigurationRangeSlider
        value={value}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        onValueChange={onValueChange}
        onActivate={onClick}
      />
    </div>
  );
}
